import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import type { SetupState } from "@dentacore/shared-types";

import { readSetupState } from "../api/setup";
import { useAuth } from "../auth/auth-context";
import { Modal } from "../ui/modal";
import styles from "./setup.module.css";
import { useCan } from "../auth/use-can";

/**
 * The first thing a new practice sees — what has to be filled in before the
 * product does anything, and why.
 *
 * ── Why a dialog, when there is already a checklist ─────────────────────────
 *
 * `/setup` lists the four steps and links into the screens that own them. It is
 * a good *worklist* and a poor *introduction*: it assumes you already know what
 * a location, a chair, a provider and a procedure are to DentaCore, and that
 * the four are prerequisites rather than a menu. Somebody who has just bought a
 * subscription knows none of that, and the checklist's own wording cannot carry
 * it without becoming an essay on a page meant for ticking things off.
 *
 * So this says it once, in front of everything, and then gets out of the way.
 *
 * ── It appears once, and dismissal is per-browser on purpose ────────────────
 *
 * **This is the one place `localStorage` is right, and it is worth saying why,
 * because decision G4 rejected it for the banner.** G4's objection was that a
 * dismissal stored per browser means a task list dismissed on a laptop is still
 * showing at the front desk — true, and fatal for a *banner*, which exists to
 * keep saying what is outstanding. A greeting is a different thing: it should
 * appear once per person, and a receptionist opening the app on another machine
 * getting a one-off explanation of what setup is for is the intended behaviour
 * rather than a leak.
 *
 * The banner stays exactly as G4 built it — computed live, and undismissable.
 * That is the part that nags; this is the part that explains.
 *
 * **Keyed by clinic and user**, so signing a second practice in on the same
 * browser greets them too, and re-seeding a development database does not leave
 * a dialog permanently suppressed.
 *
 * ── When it does not appear ─────────────────────────────────────────────────
 *
 * Not for a non-admin, who cannot act on any of it (S3). Not for a clinic that
 * is already set up. And never if the check fails — an unreachable server must
 * not put a modal in the way of a clinic that is working perfectly well.
 *
 * **It does show on `/setup`**, which is where a new admin lands, and that is
 * the point: the checklist assumes you know what the four things are, and this
 * is the sentence that tells you.
 */
export function WelcomeDialog(): React.JSX.Element | null {
  const { user } = useAuth();
  const can = useCan();
  const navigate = useNavigate();
  const [state, setState] = useState<SetupState | null>(null);
  const [dismissed, setDismissed] = useState(false);

  const isAdmin = can("settings.clinic.manage");
  const key = user === null ? null : `dentacore.welcome.${user.clinicId}.${user.id}`;

  useEffect(() => {
    if (!isAdmin || key === null) return;

    // Already greeted on this browser. Read before the request, so a returning
    // admin never sees a flash of dialog while the check is in flight.
    try {
      if (localStorage.getItem(key) !== null) {
        setDismissed(true);
        return;
      }
    } catch {
      // Private mode, or storage disabled. Then it greets every time, which is
      // a nuisance rather than a fault — and better than not greeting at all.
    }

    const controller = new AbortController();
    readSetupState(controller.signal)
      .then(setState)
      .catch(() => {
        /* Silent by design — see above. */
      });

    return () => controller.abort();
  }, [isAdmin, key]);

  function close(): void {
    setDismissed(true);
    try {
      if (key !== null) localStorage.setItem(key, new Date().toISOString());
    } catch {
      // The dialog simply reappears next time. Nothing is lost.
    }
  }

  if (!isAdmin || dismissed || state === null || state.ready) return null;

  const done = (key: string): boolean => state.steps.find((s) => s.key === key)?.done === true;

  return (
    <Modal
      label="Welcome to DentaCore"
      onClose={close}
      testId="welcome-dialog"
      panelClassName={styles.welcomePanel}
    >
      <div className={styles.welcome}>
        <p className={styles.welcomeEyebrow}>Welcome to DentaCore</p>
        <h2 className={styles.welcomeTitle}>
          {user?.clinicName} needs four things before it can take a booking
        </h2>
        <p className={styles.welcomeLede}>
          Nothing here is a form to fill in now &mdash; each one is a real part of your practice,
          and you can change any of it later. Until all four exist, the calendar has no columns to
          draw and there is nothing to book, which is why the app looks empty.
        </p>

        {/*
          **The four required steps, in the order they depend on each other.**
          A chair belongs to a location, a procedure belongs to a location's own
          catalogue, and a provider belongs to the clinic — so the order is not
          presentational, it is what you can do next.
        */}
        <ol className={styles.welcomeSteps}>
          <Step
            n={1}
            done={done("locations")}
            title="A location"
            body="The practice itself, or one of them if you run several. It carries its own timezone
                  and its opening hours."
          />
          <Step
            n={2}
            done={done("chairs")}
            title="At least one chair"
            body="A treatment room. Each chair becomes a column on the day grid, so this is what the
                  calendar is actually made of, and each one carries its own turnover — the gap the
                  calendar leaves after every appointment in it. One is enough to start."
          />
          <Step
            n={3}
            done={done("providers")}
            title="A dentist or a hygienist"
            body="Somebody who performs treatment — you, if you are the dentist. An appointment needs a
                  provider, and only these two roles can be one."
          />
          <Step
            n={4}
            done={done("procedures")}
            title="Your procedures, with prices"
            body="What you offer and what it costs, per location. Every booking and every treatment-plan
                  step is picked from this list, and it is where their price comes from — so nothing can
                  be typed differently twice."
          />
        </ol>

        <p className={styles.welcomeNote}>
          Opening hours are worth setting too: they decide where the calendar&rsquo;s day starts and
          ends, and never refuse a booking outside them.
        </p>

        <div className={styles.welcomeActions}>
          <button
            type="button"
            className={styles.welcomePrimary}
            onClick={() => {
              close();
              void navigate("/setup");
            }}
            data-testid="welcome-start"
          >
            Start setting up
          </button>
          <button
            type="button"
            className={styles.welcomeGhost}
            onClick={close}
            data-testid="welcome-later"
          >
            Look around first
          </button>
        </div>
        <p className={styles.welcomeNote}>
          The bar at the top keeps track of what is left, on every screen, until it is done.
        </p>
      </div>
    </Modal>
  );
}

/** One step, with a tick once the clinic actually has it. */
function Step({
  n,
  done,
  title,
  body,
}: {
  n: number;
  done: boolean;
  title: string;
  body: string;
}): React.JSX.Element {
  return (
    <li className={done ? `${styles.welcomeStep} ${styles.welcomeStepDone}` : styles.welcomeStep}>
      <span className={styles.welcomeStepMark} aria-hidden="true">
        {done ? "✓" : n}
      </span>
      <span className={styles.welcomeStepBody}>
        <b>{title}</b>
        <span>{body}</span>
      </span>
      {/* Said in text as well as colour, because the tick is the only other cue. */}
      {done && <span className={styles.welcomeStepState}>Done</span>}
    </li>
  );
}
